import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setErrorAction } from "../store/reducers/commonSlice";
import {
  setViewsAction,
  setRevisionsAction,
  setRevisionAction,
  addRevisionsAction,
} from "../store/reducers/searchSlice";
import endpoints from "./../endpoints";
import IOption from "../types/option";
import IView from "../types/view";
import moment from "moment";
import IRevision from "../types/revision";
import { AuthService } from "../services/AuthService";

interface ICheckPageResponse {
  query: {
    pages: {
      title: string;
      pageid?: string;
      missing?: boolean;
      fullurl: string;
    }[];
  };
}

interface IGetViewsDataRequest {
  query: string;
  dateFrom: string;
  dateTo: string;
  type: string;
}

interface IGetRevisionsRequest {
  pageId: number;
  limit: number;
  fromRevisionId?: string;
}

export type TPageResponse = {
  id: number;
  title: string;
  url: string;
  lastRevisionId?: number;
  redirectedFrom?: string;
};

interface ICheckRedirectResponse {
  page: TPageResponse;
  talk?: TPageResponse;
}

interface ICheckRedirectRequest {
  titles: string[];
  withRedirect: boolean;
}

interface IGetRevisionResponse {
  content?: string;
  notices?: string[];
  importance?: string;
}

export const wikiAPI = createApi({
  reducerPath: "wikiAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: "",
  }),
  endpoints: (builder) => ({
    searchAutoComplete: builder.mutation<IOption[], string>({
      query: (query) => {
        return {
          url: endpoints.wikiSearchApiUrl,
          method: "GET",
          params: {
            q: query,
            limit: 20,
          },
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data: result, meta } = await queryFulfilled;
        } catch (e: any) {
          dispatch(setErrorAction("Search error"));
        }
      },
      transformResponse: (response: any) => {
        if (!response.pages) return [];
        return response.pages.reduce(
          (accum: IOption[], cur: any, index: number) => {
            accum.push({
              id: cur.id,
              title: cur.title,
              url: "",
              slug: cur.key,
              img: cur.thumbnail ? cur.thumbnail.url : "",
            });
            return accum;
          },
          []
        );
      },
    }),
    checkRedirect: builder.mutation<
      ICheckRedirectResponse | null,
      ICheckRedirectRequest
    >({
      query: (data) => {
        const queryData: any = {
          url: endpoints.wikiApi,
          method: "GET",
          params: {
            action: "query",
            prop: "info",
            inprop: "talkid|url",
            format: "json",
            titles: data.titles.join("|"),
            origin: "*",
          },
        };

        if (data.withRedirect) {
          queryData.params.redirects = "";
        }

        return queryData;
      },
      transformResponse: (response: any) => {
        if (
          !response["query"] ||
          !response["query"]["pages"] ||
          typeof response["query"]["pages"] !== "object"
        )
          return null;

        const pages = response["query"]["pages"];
        const pageIds = Object.keys(pages);
        if (!pageIds.length) return null;

        let page = null;
        let talkPage = null;

        if (pageIds.length === 1) {
          page = pages[pageIds[0]];
        } else {
          if (pages[pageIds[0]]["title"].indexOf("Talk:") !== 0) {
            page = pages[pageIds[0]];
            talkPage = pages[pageIds[1]];
          } else {
            page = pages[pageIds[1]];
            talkPage = pages[pageIds[0]];
          }
        }

        if (!page) return null;

        const data: ICheckRedirectResponse = {
          page: {
            id: +page["pageid"],
            title: page["title"],
            url: page["fullurl"],
            lastRevisionId: +page["lastrevid"],
          },
        };

        if (talkPage) {
          data.talk = {
            id: +talkPage["pageid"],
            title: talkPage["title"],
            url: talkPage["fullurl"],
            lastRevisionId: +talkPage["lastrevid"],
          };
        }

        const redirects = response["query"]["redirects"];
        if (Array.isArray(redirects) && redirects.length > 0) {
          const pageRedirect =
            redirects[0]["from"].indexOf("Talk:") !== 0
              ? redirects[0]
              : redirects[1];
          const talkPageRedirect =
            redirects[0]["from"].indexOf("Talk:") !== 0
              ? redirects[1]
              : redirects[0];

          if (pageRedirect["from"] !== pageRedirect["to"]) {
            data.page.redirectedFrom = pageRedirect["from"];
          }

          if (
            data.talk &&
            talkPageRedirect &&
            talkPageRedirect["from"] !== talkPageRedirect["to"]
          ) {
            data.talk.redirectedFrom = talkPageRedirect["from"];
          }
        }

        return data;
      },
    }),
    getViewsData: builder.mutation<IView[], IGetViewsDataRequest>({
      query: (data) => {
        const period =
          data.type !== "monthly" && data.type !== "daily"
            ? "monthly"
            : data.type;

        const url = endpoints.getViews
          .replace("{slug}", data.query)
          .replace("{from}", data.dateFrom)
          .replace("{to}", data.dateTo)
          .replace("{period}", period);

        return {
          url: url,
          method: "GET",
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data: result, meta } = await queryFulfilled;

          dispatch(setViewsAction(result));
        } catch (e: any) {
          dispatch(setViewsAction([]));
          // dispatch(setErrorAction('View stat error'))
        }
      },
      transformResponse: (response: any) => {
        if (!response["items"]) {
          return [];
        }

        return response["items"].reduce((accum: IView[], cur: any) => {
          const timeStamp = cur["timestamp"];

          accum.push({
            count: cur["views"],
            date: `${timeStamp[6]}${timeStamp[7]}/${timeStamp[4]}${timeStamp[5]}/${timeStamp[0]}${timeStamp[1]}${timeStamp[2]}${timeStamp[3]}`,
          });
          return accum;
        }, []);
      },
    }),
    getRevisions: builder.mutation<IRevision[], IGetRevisionsRequest>({
      query: (data) => {
        const url = endpoints.wikiApi;

        const params: { [key: string]: string | number } = {
          action: "query",
          pageids: data.pageId,
          prop: "info|revisions",
          inprop: "url|talkid",
          rvprop: "ids|timestamp|user|size|tags",
          rvlimit: data.limit ?? 10,
          origin: "*",
          format: "json",
        };

        if (data.fromRevisionId) {
          params.rvstartid = data.fromRevisionId;
        }

        return {
          url: url,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          const { data: revisionList, meta } = await queryFulfilled;

          if (data.fromRevisionId) {
            dispatch(addRevisionsAction(revisionList));
          } else {
            dispatch(setRevisionsAction(revisionList));
            if (!revisionList.length) return;

            dispatch(
              wikiAPI.endpoints.getRevisionContent.initiate(revisionList[0])
            );
          }
        } catch (e: any) {
          // dispatch(setRevisionsAction([]))
          // dispatch(setErrorAction('Revisions error'))
        }
      },
      transformResponse: (response: any) => {
        if (
          !response["query"] ||
          !response["query"]["pages"] ||
          typeof response["query"]["pages"] !== "object"
        )
          return [];

        const pages = response["query"]["pages"];
        const pageIds = Object.keys(pages);
        if (!pageIds.length) return [];

        const page = pages[pageIds[0]];
        if (!page || !page["revisions"]) return [];

        return page["revisions"].reduce((accum: IRevision[], cur: any) => {
          const dateStr = cur["timestamp"].replace("T", " ").replace("Z", "");
          accum.push({
            id: cur["revid"],
            date: moment(new Date(dateStr)).format("H:mm:ss DD/MM/YYYY"),
            username: cur["user"],
            size: cur["size"],
            parentId: cur["parentid"],
            tags: cur["tags"],
          });
          return accum;
        }, []);
      },
    }),
    getRevisionContent: builder.mutation<{ content: string }, IRevision>({
      query: (revisionData) => {
        const url = `${endpoints.api}/revisions/${revisionData.id}/content`;
        const token = AuthService.getToken() || "";

        return {
          url: url,
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      async onQueryStarted(revisionData, { dispatch, queryFulfilled }) {
        try {
          const { data, meta } = await queryFulfilled;

          const revision = {
            ...revisionData,
            content: data.content,
          };

          dispatch(setRevisionAction(revision));
        } catch (e: any) {
          dispatch(setErrorAction("Revision error"));
        }
      },
    }),

    getRevisionAnalysis: builder.mutation<IGetRevisionResponse, IRevision>({
      query: (revisionData) => {
        const url = `${endpoints.api}/revisions/${revisionData.parentId}/${revisionData.id}/analysis`;
        const token = AuthService.getToken() || "";

        return {
          url: url,
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      async onQueryStarted(revisionData, { dispatch, queryFulfilled }) {
        try {
          const { data: revisionResult, meta } = await queryFulfilled;

          const revision = {
            ...revisionData,
            content: revisionResult.content,
            notices: revisionResult.notices,
            importance: revisionResult.importance,
          };

          dispatch(setRevisionAction(revision));
        } catch (e: any) {
          dispatch(setErrorAction("Revision error"));
        }
      },
    }),

    checkPageExists: builder.mutation<IOption[], string>({
      query: (pageSlug) => {
        return {
          url: endpoints.wikiApi,
          method: "GET",
          params: {
            action: "query",
            redirects: "",
            format: "json",
            titles: pageSlug,
            formatversion: 2,
            prop: "info",
            inprop: "url",
            origin: "*",
          },
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data: result, meta } = await queryFulfilled;
        } catch (e: any) {}
      },
      transformResponse: (response: ICheckPageResponse) => {
        if (
          !response["query"] ||
          !Array.isArray(response["query"]["pages"]) ||
          !response["query"]["pages"].length
        )
          return [];

        const pageData = response["query"]["pages"][0];
        if (pageData["missing"] === true || !pageData["pageid"]) return [];

        let pageTitle = pageData["title"];

        return [
          {
            title: pageData["title"],
            url: pageData["fullurl"],
            slug: "",
          },
        ];
      },
    }),
    getRevisionAIAnalysis: builder.mutation<any, string>({
      query: (revisionText) => {
        const token = AuthService.getToken() || "";

        return {
          url: endpoints.api + "/ai/analyze",
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            text: revisionText,
          },
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data: result, meta } = await queryFulfilled;
        } catch (e: any) {
          dispatch(
            setErrorAction(e.error?.data?.message || "Revision analyzing error")
          );
        }
      },
      // transformResponse: (response: string) => {
      //     if (!response['query']
      //         || !Array.isArray(response['query']['pages'])
      //         || !response['query']['pages'].length
      //     ) return [];
      //
      //     const pageData = response['query']['pages'][0]
      //     if (pageData['missing'] === true || !pageData['pageid']) return [];
      //
      //     let pageTitle = pageData['title']
      //
      //     return [{
      //         title: pageData['title'],
      //         url: pageData['fullurl'],
      //         slug: ''
      //     }]
      // }
    }),
  }),
});
